/**
 * TerraObservationCards — renders unacknowledged TERRA observations as
 * dismissible cards on product dashboards.
 *
 * Embed on any product page with the relevant scope filter:
 *   <TerraObservationCards product="polygon" siteId={siteId} />
 *   <TerraObservationCards product="phreatic" />
 *   <TerraObservationCards siteId={siteId} />  // all products for a site
 *
 * The cards surface findings from the nightly proactive scan plus any
 * observations TERRA left in chat. Clicking dismiss acknowledges the
 * observation server-side so it won't re-appear.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  listObservations,
  acknowledgeObservation,
  type TerraObservation,
} from '../../api/observations';

interface Props {
  product?: string;
  siteId?: string;
  tag?: string;
  relatedEntityType?: string;
  relatedEntityId?: string;
  limit?: number;
  title?: string;
  className?: string;
}

const PRODUCT_ACCENT: Record<string, string> = {
  polygon: 'border-l-amber-500/60 bg-amber-500/5',
  phreatic: 'border-l-sky-500/60 bg-sky-500/5',
  precept: 'border-l-violet-500/60 bg-violet-500/5',
  'data-vault': 'border-l-cyan-500/60 bg-cyan-500/5',
  protocol: 'border-l-emerald-500/60 bg-emerald-500/5',
  terra: 'border-l-mono-500/60 bg-mono-500/5',
};

function formatRelative(iso: string): string {
  const created = new Date(iso).getTime();
  const now = Date.now();
  const diffMin = Math.floor((now - created) / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}d ago`;
}

export const TerraObservationCards: React.FC<Props> = ({
  product,
  siteId,
  tag,
  relatedEntityType,
  relatedEntityId,
  limit = 10,
  title,
  className = '',
}) => {
  const [items, setItems] = useState<TerraObservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dismissingId, setDismissingId] = useState<string | null>(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listObservations({
        product,
        site_id: siteId,
        tag,
        related_entity_type: relatedEntityType,
        related_entity_id: relatedEntityId,
        limit,
        include_acknowledged: false,
      });
      setItems(rows);
    } catch (e: any) {
      setError(e?.message || 'Failed to load observations');
    } finally {
      setLoading(false);
    }
  }, [product, siteId, tag, relatedEntityType, relatedEntityId, limit]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleDismiss = async (obs: TerraObservation) => {
    setDismissingId(obs.id);
    try {
      await acknowledgeObservation(obs.id, { acknowledgement: 'dismissed by user' });
      setItems((prev) => prev.filter((x) => x.id !== obs.id));
    } catch (e) {
      // Keep card visible on failure; consume error silently
      console.warn('Failed to acknowledge observation', e);
    } finally {
      setDismissingId(null);
    }
  };

  if (loading && items.length === 0) {
    return (
      <div className={`text-[10px] text-mono-500 ${className}`}>
        Loading TERRA observations...
      </div>
    );
  }

  if (error) {
    return (
      <div className={`text-[11px] text-amber-400 ${className}`}>
        TERRA observations unavailable: {error}
      </div>
    );
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <div className={`space-y-2 ${className}`}>
      {title && (
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-pulse" />
          <h3 className="text-[11px] uppercase tracking-wider text-cyan-300/90">
            {title}
          </h3>
          <span className="text-[10px] text-mono-500">({items.length})</span>
        </div>
      )}
      {items.map((obs) => {
        const accent = PRODUCT_ACCENT[obs.product] || 'border-l-mono-500/60 bg-mono-500/5';
        const isDismissing = dismissingId === obs.id;
        return (
          <div
            key={obs.id}
            className={`border-l-2 ${accent} rounded-r-md p-3 border border-mono-800 border-l-[3px]`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[9px] uppercase tracking-wider text-mono-400">
                    {obs.product}
                  </span>
                  {obs.site_name && (
                    <span className="text-[9px] text-mono-500">{obs.site_name}</span>
                  )}
                  <span className="text-[9px] text-mono-600 ml-auto">
                    {formatRelative(obs.created_at)}
                  </span>
                </div>
                <div className="text-[13px] text-mono-100 font-medium mb-1">
                  {obs.subject}
                </div>
                <div className="text-[11px] text-mono-300 leading-relaxed">
                  {obs.observation}
                </div>
                {obs.tags.length > 0 && (
                  <div className="flex gap-1 mt-2">
                    {obs.tags.slice(0, 4).map((t) => (
                      <span
                        key={t}
                        className="px-1.5 py-0.5 text-[9px] rounded bg-mono-800 text-mono-400"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => handleDismiss(obs)}
                disabled={isDismissing}
                className="text-[10px] text-mono-500 hover:text-mono-200 disabled:opacity-40 transition-colors whitespace-nowrap"
                title="Dismiss this observation"
              >
                {isDismissing ? '...' : 'Dismiss'}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TerraObservationCards;
